import React from 'react';
import { AlertTriangle, BarChart3, Clock, CreditCard, DollarSign, FileBarChart, Receipt } from 'lucide-react';

const PANEL = 'bg-white rounded-lg shadow-sm border border-gray-200 p-6';

function money(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export function DollarsAtRiskPanel({ atRisk }) {
  if (!atRisk) return null;
  const parts = atRisk.parts || [];
  const dollarParts = parts.filter((p) => p.included && p.unit === 'usd' && p.amount != null);
  const dollarTotal = dollarParts.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  return (
    <div className={PANEL}>
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Dollars at risk</h3>
      </div>
      <p className="text-3xl font-bold text-gray-900 mb-1">{money(dollarTotal)}</p>
      <p className="text-xs text-gray-500 mb-4">Sum of labeled USD parts only — not a mystery total</p>
      <ul className="space-y-2 text-sm">
        {parts.map((part) => (
          <li key={part.id} className="flex justify-between gap-3 border-b border-gray-100 pb-2">
            <span className="text-gray-600">
              {part.label}
              <span className="block text-xs text-gray-400">
                {part.ssot}
                {part.note ? ` · ${part.note}` : ''}
              </span>
            </span>
            <span className="font-semibold text-gray-900 shrink-0">
              {!part.included
                ? 'Omitted'
                : part.unit === 'count'
                  ? `${part.amount} claims`
                  : money(part.amount)}
            </span>
          </li>
        ))}
      </ul>
      {(atRisk.topCarcByDollar || []).length > 0 ? (
        <div className="mt-4">
          <p className="text-xs uppercase text-gray-500 mb-2">Top 3 CARC by $ (Remittance Advice)</p>
          <ul className="text-sm space-y-1">
            {atRisk.topCarcByDollar.map((row) => (
              <li key={row.carc} className="flex justify-between">
                <span className="font-mono">{row.carc}</span>
                <span>{money(row.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-gray-400 mt-4">Top CARC by $ appears when RA analytics is reachable</p>
      )}
    </div>
  );
}

export function AgingComposePanel({ aging }) {
  if (!aging) return null;
  return (
    <div className={PANEL}>
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Aging (compose)</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-xs uppercase text-gray-500">{aging.payer.label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {aging.payer.daysInAr != null ? `${Number(aging.payer.daysInAr).toFixed(1)}d` : '—'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Outstanding {money(aging.payer.outstanding)}</p>
          <p className="text-xs text-gray-400 mt-1">{aging.payer.formula}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-gray-500">{aging.patient.label}</p>
          <p className="text-sm text-gray-500 mt-2 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            {aging.patient.note || 'Patient buckets'}
          </p>
        </div>
      </div>
    </div>
  );
}

const DEEP_DIVE_ICONS = {
  'ar-posting-reports': CreditCard,
  'remittance-reports': Receipt,
  analytics: BarChart3,
};

export function FinanceDeepDivePanel({ links = [], onOpen }) {
  if (!links.length) return null;

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileBarChart className="w-5 h-5 text-blue-600" />
          Analytics and Reports
        </h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Open source reports and analytics yourself. AR posting, remittance, and analytics are separate.
        </p>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {links.map((link) => {
          const Icon = DEEP_DIVE_ICONS[link.id] || FileBarChart;
          return (
            <button
              key={link.id}
              type="button"
              onClick={() => onOpen?.(link.path)}
              className="text-left rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900">{link.title}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">{link.description}</p>
              <span className="text-sm font-medium text-blue-700">{link.cta} →</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
